import React, { useState, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";
import "./catalog-sale-dates.css";

const CatalogSaleDates = ({ onSelect, availableDates = null, onlyAvailable = false, onWeekChange, initialDate = null }) => {
  const initialSelectedDate = initialDate ? dayjs(initialDate) : null;
  const appliedInitialDateRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [dates, setDates] = useState([]);
  const getWeekStart = (date) => {
    const day = date.day();
    return date.subtract(day === 0 ? 6 : day - 1, "day").startOf("day");
  };

  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(initialSelectedDate || dayjs()));

  const getMonthName = (date) => {
    const monthNames = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return monthNames[date.month()];
  };

  const getWeekMonthLabel = () => {
    const firstDay = currentWeekStart;
    const lastDay = currentWeekStart.add(6, "day");
    const firstMonth = firstDay.month();
    const lastMonth = lastDay.month();

    let monthLabel = "";
    if (firstMonth === lastMonth) {
      monthLabel = getMonthName(firstDay);
    } else {
      monthLabel = `${getMonthName(firstDay)} - ${getMonthName(lastDay)}`;
    }

    return monthLabel;
  };

  const getSelectedDateLabel = () => {
    if (selectedDate) {
      return selectedDate.format("DD.MM.YYYY");
    }
    return null;
  };

  const isDatePast = (date) => {
    const today = dayjs().startOf("day");
    return date.isBefore(today, "day");
  };

  const getDateValue = useCallback((date) => date.format("YYYY-MM-DD"), []);

  const isDateAvailable = useCallback((date) => {
    if (!onlyAvailable || !availableDates) {
      return true;
    }
    return availableDates.includes(getDateValue(date));
  }, [availableDates, getDateValue, onlyAvailable]);

  useEffect(() => {
    if (!initialDate) {
      return;
    }

    const date = dayjs(initialDate);
    const dateValue = getDateValue(date);
    if (appliedInitialDateRef.current === dateValue) {
      return;
    }

    const nextWeekStart = getWeekStart(date);
    setCurrentWeekStart((prev) => prev.isSame(nextWeekStart, "day") ? prev : nextWeekStart);
    if (isDatePast(date) || !isDateAvailable(date)) {
      return;
    }

    appliedInitialDateRef.current = dateValue;
    setSelectedDate((prev) => prev && prev.isSame(date, "day") ? prev : date);
    if (onSelect) {
      onSelect({ date: date });
    }
  }, [getDateValue, initialDate, isDateAvailable, onSelect]);

  const generateDates = useCallback(() => {
    const datesList = [];
    const weekDayNames = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"];
    for (let i = 0; i < 7; i++) {
      const date = currentWeekStart.add(i, "day");
      const dayOfWeek = date.day(); // 0 = воскресенье, 1 = понедельник, ..., 6 = суббота
      const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek; // Конвертируем в ISO: 1 = понедельник, 7 = воскресенье
      const isWeekend = isoDayOfWeek === 6 || isoDayOfWeek === 7; // СБ или ВС
      const isPast = isDatePast(date);
      const available = isDateAvailable(date);
      datesList.push({
        date: date,
        day: date.date(),
        dayName: weekDayNames[isoDayOfWeek - 1],
        isWeekend: isWeekend,
        isPast: isPast,
        isAvailable: available,
      });
    }
    setDates(datesList);
    const selectedDateInWeek = selectedDate && datesList.some((d) => selectedDate.isSame(d.date, "day"));
    const selectedDateAvailable = selectedDateInWeek && !isDatePast(selectedDate) && isDateAvailable(selectedDate);
    if (datesList.length > 0 && (!selectedDate || (onlyAvailable && !selectedDateAvailable))) {
      const firstAvailableDate = datesList.find((d) => !d.isPast && d.isAvailable);
      if (firstAvailableDate) {
        const date = firstAvailableDate.date;
        setSelectedDate(date);
        if (onSelect) {
          onSelect({ date: date });
        }
      }
    }
  }, [currentWeekStart, isDateAvailable, onSelect, onlyAvailable, selectedDate]);

  useEffect(() => {
    generateDates();
  }, [generateDates]);

  useEffect(() => {
    if (onWeekChange) {
      onWeekChange(currentWeekStart, currentWeekStart.add(6, "day"));
    }
  }, [currentWeekStart, onWeekChange]);

  const handleDateSelect = (date) => {
    if (isDatePast(date) || !isDateAvailable(date)) {
      return;
    }
    setSelectedDate(date);
    if (onSelect) {
      onSelect({
        date: date,
      });
    }
  };

  const handlePrevWeek = () => {
    const newWeekStart = currentWeekStart.subtract(7, "day");
    const todayWeekStart = getWeekStart(dayjs());
    if (newWeekStart.isSame(todayWeekStart, "day") || newWeekStart.isAfter(todayWeekStart, "day")) {
      setCurrentWeekStart(newWeekStart);
    }
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(currentWeekStart.add(7, "day"));
  };

  const canGoPrev = () => {
    const todayWeekStart = getWeekStart(dayjs());
    return currentWeekStart.isAfter(todayWeekStart, "day");
  };

  return (
    <div className="catalog-sale-dates">
      <div className="catalog-sale-dates-month-label">
        <span className="catalog-sale-dates-month-name">{getWeekMonthLabel()}</span>
        {getSelectedDateLabel() && (
          <span className="catalog-sale-dates-selected-date">{getSelectedDateLabel()}</span>
        )}
      </div>
      <div className="catalog-sale-dates-header">
        <button
          className="catalog-sale-dates-nav"
          onClick={handlePrevWeek}
          disabled={!canGoPrev()}
        >
          ‹
        </button>
        <div className="catalog-sale-dates-dates">
          {dates.map((d, index) => (
            <div key={index} className="catalog-sale-dates-date-container">
              <div className={`catalog-sale-dates-day-name ${d.isWeekend ? "weekend" : ""} ${d.isPast || !d.isAvailable ? "past" : ""}`}>
                {d.dayName}
              </div>
              <button
                className={`catalog-sale-dates-date ${selectedDate && selectedDate.isSame(d.date, "day") ? "selected" : ""} ${d.isPast ? "past" : ""} ${!d.isAvailable ? "unavailable" : ""}`}
                onClick={() => handleDateSelect(d.date)}
                disabled={d.isPast || !d.isAvailable}
              >
                {d.day}
              </button>
            </div>
          ))}
        </div>
        <button className="catalog-sale-dates-nav" onClick={handleNextWeek}>
          ›
        </button>
      </div>
    </div>
  );
};

export default CatalogSaleDates;

